'use client';

import {
  forwardRef,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  type ReactNode,
} from 'react';
import styles from './HoverPhotoCards.module.css';

const HOLD_MS = 200;
const MOVE_CANCEL_PX = 10;

export type HoverPhotoOverlayImage = {
  src: string;
  width: number;
  height: number;
  alt?: string;
};

export type HoverPhotoCard = {
  key: string;
  title: string;
  body: string;
  image: {
    mobile: string;
    desktop?: string;
    alt: string;
  };
  /** Custom overlay, such as the how-it-works panels. */
  overlay?: ReactNode;
  /** Hover graphic shown in the blur scrim. */
  overlayImage?: HoverPhotoOverlayImage;
  /** Design size of a custom overlay. An overlay image uses its own width and height. */
  overlaySize?: { width: number; height: number };
};

export type HoverPhotoCardsProps = {
  titleId: string;
  id?: string;
  eyebrow: string;
  title: string;
  titleMobile?: string;
  body: string;
  bodyMobile?: string;
  cards: HoverPhotoCard[];
  reveal?: 'hiw';
};

type RevealKind = 'full' | 'subtle' | 'group';

function revealProps(
  reveal: HoverPhotoCardsProps['reveal'],
  kind: RevealKind,
): Record<string, string | boolean> {
  if (reveal !== 'hiw') return {};
  if (kind === 'group') return { 'data-hiw-reveal-group': true };
  return { 'data-hiw-reveal': kind };
}

export const HoverPhotoCards = forwardRef<HTMLElement, HoverPhotoCardsProps>(
  function HoverPhotoCards(
    {
      titleId,
      id,
      eyebrow,
      title,
      titleMobile,
      body,
      bodyMobile,
      cards,
      reveal,
    },
    ref,
  ) {
    return (
      <section
        ref={ref}
        id={id}
        className={styles.section}
        aria-labelledby={titleId}
      >
        <div className={styles.inner}>
          <header className={styles.intro} {...revealProps(reveal, 'group')}>
            <p className={styles.eyebrow} {...revealProps(reveal, 'subtle')}>
              {eyebrow}
            </p>
            <h2
              id={titleId}
              className={styles.title}
              {...revealProps(reveal, 'full')}
            >
              {titleMobile ? (
                <>
                  <span className={styles.titleDesktop}>{title}</span>
                  <span className={styles.titleMobile}>{titleMobile}</span>
                </>
              ) : (
                title
              )}
            </h2>
            {bodyMobile ? (
              <>
                <p
                  className={`${styles.body} ${styles.bodyDesktop}`}
                  {...revealProps(reveal, 'subtle')}
                >
                  {body}
                </p>
                <p
                  className={`${styles.body} ${styles.bodyMobile}`}
                  {...revealProps(reveal, 'subtle')}
                >
                  {bodyMobile}
                </p>
              </>
            ) : (
              <p className={styles.body} {...revealProps(reveal, 'subtle')}>
                {body}
              </p>
            )}
          </header>

          <ul className={styles.grid}>
            {cards.map((card) => (
              <PhotoCard key={card.key} card={card} reveal={reveal} />
            ))}
          </ul>
        </div>
      </section>
    );
  },
);

function PhotoCard({
  card,
  reveal,
}: {
  card: HoverPhotoCard;
  reveal: HoverPhotoCardsProps['reveal'];
}) {
  const [dismissed, setDismissed] = useState(false);
  const [holding, setHolding] = useState(false);
  const [latched, setLatched] = useState(false);
  const cardRef = useRef<HTMLLIElement>(null);
  const holdTimer = useRef<number | null>(null);
  const start = useRef<{ x: number; y: number } | null>(null);
  const activePointer = useRef<number | null>(null);
  const touched = useRef(false);
  const held = useRef(false);

  const clearTimer = () => {
    if (holdTimer.current != null) {
      window.clearTimeout(holdTimer.current);
      holdTimer.current = null;
    }
  };

  const endHold = () => {
    clearTimer();
    start.current = null;
    activePointer.current = null;
    setHolding(false);
  };

  useEffect(() => clearTimer, []);

  useEffect(() => {
    if (!latched) return undefined;
    const closeOutside = (event: globalThis.PointerEvent) => {
      if (!cardRef.current?.contains(event.target as Node)) setLatched(false);
    };
    document.addEventListener('pointerdown', closeOutside);
    return () => document.removeEventListener('pointerdown', closeOutside);
  }, [latched]);

  const isOnMedia = (target: EventTarget | null) =>
    target instanceof Element && Boolean(target.closest(`.${styles.media}`));

  const onPointerDown = (event: PointerEvent<HTMLLIElement>) => {
    touched.current = event.pointerType === 'touch';
    if (!touched.current || !isOnMedia(event.target)) return;
    held.current = false;
    activePointer.current = event.pointerId;
    start.current = { x: event.clientX, y: event.clientY };
    clearTimer();
    holdTimer.current = window.setTimeout(() => {
      holdTimer.current = null;
      held.current = true;
      setHolding(true);
    }, HOLD_MS);
  };

  const onPointerMove = (event: PointerEvent<HTMLLIElement>) => {
    if (event.pointerId !== activePointer.current || !start.current) return;
    if (holdTimer.current == null) return;
    const dx = event.clientX - start.current.x;
    const dy = event.clientY - start.current.y;
    if (dx * dx + dy * dy > MOVE_CANCEL_PX * MOVE_CANCEL_PX) endHold();
  };

  // A touch tap toggles the overlay; a hold only peeks and its release click is ignored.
  const onClick = (event: MouseEvent<HTMLLIElement>) => {
    if (!touched.current || !isOnMedia(event.target)) return;
    if (held.current) {
      held.current = false;
      return;
    }
    setDismissed(false);
    setLatched((open) => !open);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLLIElement>) => {
    if (event.key !== 'Escape') return;
    setDismissed(true);
    setLatched(false);
  };

  const overlaySize =
    card.overlaySize ??
    (card.overlayImage
      ? { width: card.overlayImage.width, height: card.overlayImage.height }
      : undefined);
  const overlayStyle: CSSProperties | undefined = overlaySize
    ? {
        ['--overlay-w' as string]: `${overlaySize.width}px`,
        ['--overlay-h' as string]: `${overlaySize.height}px`,
      }
    : undefined;
  const overlay =
    card.overlay ??
    (card.overlayImage ? (
      <img
        className={styles.overlayAsset}
        src={card.overlayImage.src}
        alt={card.overlayImage.alt ?? ''}
        width={card.overlayImage.width}
        height={card.overlayImage.height}
      />
    ) : null);

  return (
    <li
      ref={cardRef}
      className={styles.card}
      data-photo-card=""
      data-open={holding || latched ? '' : undefined}
      data-overlay-dismissed={dismissed ? '' : undefined}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endHold}
      onPointerCancel={endHold}
      onPointerLeave={endHold}
      onClick={onClick}
      onContextMenu={(event) => {
        if (isOnMedia(event.target)) event.preventDefault();
      }}
      onMouseLeave={() => setDismissed(false)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setDismissed(false);
        }
      }}
      onKeyDown={onKeyDown}
    >
      <div className={styles.media} tabIndex={0} aria-label={card.title}>
        <picture className={styles.picture}>
          {card.image.desktop ? (
            <source media="(min-width: 992px)" srcSet={card.image.desktop} />
          ) : null}
          <img
            className={styles.image}
            src={card.image.mobile}
            alt={card.image.alt}
            width={464}
            height={560}
            loading="lazy"
            decoding="async"
          />
        </picture>
        {overlay ? (
          <div className={styles.scrim}>
            <div className={styles.overlay} style={overlayStyle}>
              {overlay}
            </div>
          </div>
        ) : null}
      </div>
      <div className={styles.caption} {...revealProps(reveal, 'group')}>
        <h3
          className={styles.cardTitle}
          {...(reveal === 'hiw' ? revealProps(reveal, 'full') : {})}
        >
          {card.title}
        </h3>
        <p
          className={styles.cardBody}
          {...(reveal === 'hiw' ? revealProps(reveal, 'subtle') : {})}
        >
          {card.body}
        </p>
      </div>
    </li>
  );
}
